import { logger } from '@vagabondinternet/logging';
import { WordsHappenKeyboardLayoutProvider, WordsHappenMotionProvider } from '@words-happen/ui-components';
import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  type AccountSettings,
  type AccountSettingsApi,
  createAccountSettingsApi,
  DEFAULT_ACCOUNT_SETTINGS,
  isUnauthenticatedError,
} from '../api/settings';
import { useSession } from '../session/session-context';
import { type AccountSettingsCache, storageAccountSettingsCache } from './account-settings-cache';

export type AccountSettingKey = keyof AccountSettings;

/**
 * - `guest`: signed out; the settings are the defaults and can't be changed.
 * - `loading`: signed in, waiting for the API; the settings are the device's cache, or the defaults.
 * - `ready`: loaded; the settings can be changed.
 * - `load-error`: loading failed; the settings are the cache or the defaults until `retryLoad` succeeds.
 */
export type AccountSettingsStatus = 'guest' | 'loading' | 'ready' | 'load-error';

export interface AccountSettingsContextValue {
  status: AccountSettingsStatus;
  /** The settings in effect: the saved ones, with any change still being saved shown already. */
  settings: AccountSettings;
  /** The settings with a save in flight. */
  saving: ReadonlySet<AccountSettingKey>;
  /** The settings whose last save failed, with the value that wasn't saved, for `retry`. */
  failed: Partial<AccountSettings>;
  /**
   * Shows `value` at once and saves it. A change to the value already in effect is ignored. If the save fails, the
   * setting goes back to its saved value and is listed in `failed`; if the session has expired, the player is sent to
   * sign in.
   */
  update: <K extends AccountSettingKey>(key: K, value: AccountSettings[K]) => void;
  /** Saves the value that failed for `key` again. */
  retry: (key: AccountSettingKey) => void;
  /** Loads the settings again after `load-error`. */
  retryLoad: () => void;
}

export interface AccountSettingsDependencies {
  api: AccountSettingsApi;
  cache: AccountSettingsCache;
}

const browserDependencies = (): AccountSettingsDependencies => ({
  api: createAccountSettingsApi(),
  cache: storageAccountSettingsCache(),
});

const AccountSettingsContext = createContext<AccountSettingsContextValue | undefined>(undefined);

export interface AccountSettingsProviderProps {
  children: React.ReactNode;
  /** Replaces the API and the device cache. */
  dependencies?: Partial<AccountSettingsDependencies>;
  /** Keeps `data-wh-motion` on `<html>` in step with Reduced motion. Off in showcases. */
  syncDocument?: boolean;
}

const withoutKey = <T extends object>(record: T, key: keyof T): T => {
  const { [key]: _removed, ...rest } = record;
  return rest as T;
};

/**
 * The signed-in player's account settings for the whole game app, applied below it: Reduced motion goes to
 * `WordsHappenMotionProvider` and the keyboard layout to `WordsHappenKeyboardLayoutProvider`, so every game's on-screen
 * keyboard follows a change at once, mid-game included. They load once when a signed-in player opens the app; until then, and when the API is
 * slow or cold-starting, the last settings loaded on this device are used, so rendering is never blocked. Signed out,
 * the settings are the defaults and the device's cache is cleared.
 */
export const AccountSettingsProvider: React.FC<AccountSettingsProviderProps> = ({
  children,
  dependencies,
  syncDocument = true,
}) => {
  const [{ api, cache }] = useState(() => ({ ...browserDependencies(), ...dependencies }));
  const { signedIn, goToSignIn } = useSession();
  const [saved, setSaved] = useState<AccountSettings>(() => (signedIn && cache.read()) || DEFAULT_ACCOUNT_SETTINGS);
  const [loadStatus, setLoadStatus] = useState<'loading' | 'ready' | 'load-error'>('loading');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [pending, setPending] = useState<Partial<AccountSettings>>({});
  const [failed, setFailed] = useState<Partial<AccountSettings>>({});
  /** The latest save per setting: only its outcome changes what is shown. */
  const latestSave = useRef<Partial<Record<AccountSettingKey, number>>>({});

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new loadAttempt is how retryLoad loads again
  useEffect(() => {
    if (!signedIn) {
      cache.clear();
      setSaved(DEFAULT_ACCOUNT_SETTINGS);
      setPending({});
      setFailed({});
      return;
    }

    let current = true;
    setLoadStatus('loading');
    api.load().then(
      (settings) => {
        if (current) {
          cache.write(settings);
          setSaved(settings);
          setLoadStatus('ready');
        }
      },
      (error: unknown) => {
        logger.warn({ message: 'Loading the account settings failed', error: (error as Error).message });
        if (current) {
          setLoadStatus('load-error');
        }
      },
    );
    return () => {
      current = false;
    };
  }, [signedIn, loadAttempt, api, cache]);

  const status: AccountSettingsStatus = signedIn ? loadStatus : 'guest';
  const settings = useMemo<AccountSettings>(
    () => (signedIn ? { ...saved, ...pending } : DEFAULT_ACCOUNT_SETTINGS),
    [signedIn, saved, pending],
  );

  const save = useCallback(
    <K extends AccountSettingKey>(key: K, value: AccountSettings[K]) => {
      const attempt = (latestSave.current[key] ?? 0) + 1;
      latestSave.current[key] = attempt;
      const isLatest = () => latestSave.current[key] === attempt;

      setPending((current) => ({ ...current, [key]: value }));
      setFailed((current) => withoutKey(current, key));

      api.save({ [key]: value }).then(
        (result) => {
          // An earlier save finishing after a later one would show a value the player has already changed.
          if (!isLatest()) {
            return;
          }
          cache.write(result);
          setSaved(result);
          setPending((current) => withoutKey(current, key));
        },
        (error: unknown) => {
          if (!isLatest()) {
            return;
          }
          setPending((current) => withoutKey(current, key));
          if (isUnauthenticatedError(error)) {
            goToSignIn();
            return;
          }
          logger.warn({ message: `Saving ${key} failed`, error: (error as Error).message });
          setFailed((current) => ({ ...current, [key]: value }));
        },
      );
    },
    [api, cache, goToSignIn],
  );

  const update = useCallback(
    <K extends AccountSettingKey>(key: K, value: AccountSettings[K]) => {
      if (status === 'ready' && settings[key] !== value) {
        save(key, value);
      }
    },
    [status, settings, save],
  );

  const retry = useCallback(
    (key: AccountSettingKey) => {
      const value = failed[key];
      if (status === 'ready' && value !== undefined) {
        save(key, value);
      }
    },
    [status, failed, save],
  );

  const retryLoad = useCallback(() => setLoadAttempt((attempt) => attempt + 1), []);

  const value = useMemo<AccountSettingsContextValue>(
    () => ({
      status,
      settings,
      saving: new Set(Object.keys(pending) as AccountSettingKey[]),
      failed,
      update,
      retry,
      retryLoad,
    }),
    [status, settings, pending, failed, update, retry, retryLoad],
  );

  return (
    <AccountSettingsContext.Provider value={value}>
      <WordsHappenMotionProvider preference={settings.reducedMotion} syncDocument={syncDocument}>
        <WordsHappenKeyboardLayoutProvider layout={settings.keyboardLayout}>
          {children}
        </WordsHappenKeyboardLayoutProvider>
      </WordsHappenMotionProvider>
    </AccountSettingsContext.Provider>
  );
};

export const useAccountSettings = (): AccountSettingsContextValue => {
  const value = useContext(AccountSettingsContext);
  if (!value) {
    throw new Error('useAccountSettings must be used inside an AccountSettingsProvider');
  }
  return value;
};
